import { Checkbox, NumberInput, Stack, TextInput } from '@mantine/core';
import type { Theme, ThemeOverrides } from '@quizee/core';
import {
  hasThemeOverride,
  inheritedThemeField,
  THEME_FIELDS,
  withThemeField,
  withoutThemeField,
  type Layer,
  type ThemeFieldSpec,
} from '../brand/inheritance';
import { OverrideField } from './OverrideField';
import { ReadList, ReadRow, sourceWords } from './ReadValue';

/** Every value a theme field can hold. */
type FieldValue = string | number | boolean | undefined;

/**
 * The six things a theme is, as they currently are.
 *
 * The same six fields the form holds, said as values rather than offered as inputs, each one
 * naming where it came from. A colour is written as its code and shown as itself: publishers
 * arrive with a hex code from a brand guide and with an opinion about the colour, and the swatch
 * answers the second without pretending to be the value.
 */
export function ThemeRead({
  theme,
  below,
  system,
}: {
  theme: ThemeOverrides | null;
  below: readonly Layer[];
  system: Theme;
}) {
  return (
    <ReadList>
      {THEME_FIELDS.map((spec) => {
        const overridden = hasThemeOverride(theme, spec.field);
        const inherited = inheritedThemeField(spec.field, below, system);
        const value = overridden ? (theme?.[spec.field] ?? inherited.value) : inherited.value;

        return (
          <ReadRow
            key={spec.field}
            label={spec.label}
            value={themeWords(spec.field, value)}
            swatch={spec.field.endsWith('_colour') && typeof value === 'string' ? value : undefined}
            source={sourceWords(overridden, inherited.from)}
          />
        );
      })}
    </ReadList>
  );
}

/** One theme value in words, including the two that have no value to write down. */
function themeWords(field: keyof Theme, value: FieldValue): string {
  if (field === 'show_brand_credit') {
    return value === true ? 'Shown under the interaction' : 'Not shown';
  }
  if (field === 'corner_radius') {
    const pixels = typeof value === 'number' ? value : 0;
    return pixels === 0 ? '0px (square corners)' : `${String(pixels)}px`;
  }
  if (field === 'font_family') {
    return typeof value === 'string' && value !== ''
      ? value
      : 'The typeface the widget brings with it';
  }
  return typeof value === 'string' ? value : 'Not set';
}

/**
 * The six things a theme is, each shown as inherited or set here.
 *
 * The same component serves the publisher's defaults and one brand, because they are the same six
 * fields resolved through a different number of layers. What changes is `below`: a brand has the
 * publisher under it and then the quizee default, the publisher has only the default.
 */
export function ThemeSection({
  theme,
  onChange,
  below,
  system,
  canEdit,
  idPrefix,
}: {
  /** What this level sets. Null and an empty object both mean it sets nothing. */
  theme: ThemeOverrides | null;
  onChange: (next: ThemeOverrides) => void;
  /** The layers under this one, nearest first. */
  below: readonly Layer[];
  system: Theme;
  canEdit: boolean;
  /** Keeps input ids unique when two of these ever sit on one page. */
  idPrefix: string;
}) {
  return (
    <Stack component="ul" gap="sm" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
      {THEME_FIELDS.map((spec) => {
        const overridden = hasThemeOverride(theme, spec.field);
        const inherited = inheritedThemeField(spec.field, below, system);
        const value = overridden ? (theme?.[spec.field] ?? inherited.value) : inherited.value;

        return (
          <OverrideField
            key={spec.field}
            label={spec.label}
            overridden={overridden}
            inheritedFrom={inherited.from}
            canEdit={canEdit}
            // The typeface is the one field that can inherit nothing, and an override of nothing
            // would read as no override at all, so setting your own starts from an empty box.
            onOverride={() => onChange(withThemeField(theme, spec.field, inherited.value ?? ''))}
            onInherit={() => onChange(withoutThemeField(theme, spec.field))}
          >
            <ThemeInput
              spec={spec}
              id={`${idPrefix}-${spec.field}`}
              value={value}
              editable={canEdit && overridden}
              onChange={(next) => onChange(withThemeField(theme, spec.field, next))}
            />
          </OverrideField>
        );
      })}
    </Stack>
  );
}

/**
 * One input, chosen by which field it is.
 *
 * An inherited field is shown filled in and not editable rather than blank. A blank box would say
 * "nothing here", and there is always something here: the value a reader gets today.
 */
function ThemeInput({
  spec,
  id,
  value,
  editable,
  onChange,
}: {
  spec: ThemeFieldSpec;
  id: string;
  value: FieldValue;
  editable: boolean;
  onChange: (value: FieldValue) => void;
}) {
  if (spec.field === 'show_brand_credit') {
    return (
      <Checkbox
        id={id}
        label={spec.label}
        description={spec.description}
        disabled={!editable}
        checked={value === true}
        onChange={(event) => onChange(event.currentTarget.checked)}
      />
    );
  }

  if (spec.field === 'corner_radius') {
    return (
      <NumberInput
        id={id}
        label={spec.label}
        description={spec.description}
        disabled={!editable}
        min={0}
        max={40}
        clampBehavior="strict"
        allowDecimal={false}
        value={typeof value === 'number' ? value : 0}
        onChange={(next) => onChange(typeof next === 'number' ? next : Number(next))}
        style={{ maxWidth: '12rem' }}
      />
    );
  }

  if (spec.field === 'font_family') {
    return (
      <TextInput
        id={id}
        label={spec.label}
        description={spec.description}
        disabled={!editable}
        maxLength={200}
        placeholder="The typeface the widget brings with it"
        value={typeof value === 'string' ? value : ''}
        onChange={(event) => onChange(event.currentTarget.value)}
        style={{ maxWidth: '22rem' }}
      />
    );
  }

  return (
    <ColourField
      id={id}
      label={spec.label}
      description={spec.description}
      disabled={!editable}
      value={typeof value === 'string' ? value : '#000000'}
      onChange={onChange}
    />
  );
}

/**
 * A colour, as a code you can paste and as a swatch you can open.
 *
 * Two controls for one value because publishers arrive with both habits: a brand guide gives a hex
 * code to type, and anybody else wants to see the colours. The swatch is the browser's own colour
 * input, which costs nothing to ship and is the picker the reader of this screen already knows how
 * to use.
 */
function ColourField({
  id,
  label,
  description,
  disabled,
  value,
  onChange,
}: {
  id: string;
  label: string;
  description: string;
  disabled: boolean;
  value: string;
  onChange: (value: string) => void;
}) {
  // The browser's colour input refuses anything but a full six digit hex, and a half typed value
  // in the box beside it is a normal state, so it holds the last thing that was a colour.
  const swatch = /^#[0-9a-f]{6}$/i.test(value) ? value : '#000000';

  return (
    <TextInput
      id={id}
      label={label}
      description={description}
      disabled={disabled}
      value={value}
      maxLength={7}
      spellCheck={false}
      placeholder="#1f5fd8"
      onChange={(event) => onChange(event.currentTarget.value)}
      leftSectionPointerEvents="all"
      leftSection={
        <input
          type="color"
          aria-label={`${label} picker`}
          disabled={disabled}
          value={swatch}
          onChange={(event) => onChange(event.currentTarget.value)}
          style={{
            width: 20,
            height: 20,
            padding: 0,
            border: 0,
            background: 'none',
            cursor: disabled ? 'default' : 'pointer',
          }}
        />
      }
      style={{ maxWidth: '18rem' }}
    />
  );
}
